/* jshint esversion: 6 */
import React from 'react';
import ConnectMixin from 'app/mixins/ConnectMixin.js';
import * as _ from 'underscore';
import ConnectSocialDialog from '@/shadcn/components/AddSocialModal/ConnectSocialDialog.tsx';

/*
 * Mastodon is one of two networks we collect a credential for instead of
 * round-tripping through OAuth, so it gets the connect dialog's "credentials"
 * screen — one field, and a footer that goes back or connects.
 *
 * This panel hosts its own dialog rather than being handed to SocialModal as a
 * body, because the footer here belongs to it: what Connect does, when it's
 * enabled, and where Back returns to are all decisions this component owns.
 *
 * ESCAPE now closes the dialog rather than stepping back to the account list, as
 * it did while this drew its own keydown listener. That is what every other
 * dialog in the app does with Escape, and the footer's Back is the way back.
 */

const HANDLE_FIELD = 'mastodonHandle';

class MastodonHandle extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            serviceId: this.props.app.config.constants.POST_SVC_MASTODON,
            mastodonHandle: '',
            error: ''
        };

        this.collectHandle = this.collectHandle.bind(this);
        this.connectService = ConnectMixin.connectService.bind(this);
        this.closeAction = this.closeAction.bind(this);

    }

    /*
     * `connectService` reads the service id and the direct-connect flag off the
     * clicked element's dataset, which is how the legacy button carried them.
     * The footer button belongs to the dialog and has no say in what it starts,
     * so the pair is synthesised here instead — this is the direct connect,
     * always, by the time a handle has been typed.
     */
    connectEvent() {
        return {
            preventDefault: () => {},
            currentTarget: {
                dataset: {
                    serviceid: this.state.serviceId,
                    direct: 'true'
                }
            }
        };
    }

    collectHandle() {
        let handleRE = /\@[\d\w\.\-]+\@[\d\w\.\-]+/,
            urlRE = /https?:\/\/[-A-Za-z0-9\.+&@#\/%?=~_|!:,.;]*\/@[A-Za-z0-9\.+&@#\/%?=~_|!:,.;]*/,
            error = '';

        if (!handleRE.test(this.state.mastodonHandle) && !urlRE.test(this.state.mastodonHandle)) {
            error = 'Please provide a valid Mastodon handle. Either style, @username@server or https://server/@username, can be used.';
        }

        if (!error) {
            this.connectService(this.connectEvent());
        }

        this.setState({
            error: error
        });

    }

    /*
     * Back goes to this network's account list where there is one to go back to,
     * and to the network picker otherwise. The two path checks are the surfaces
     * that have no picker behind them.
     */
    closeAction() {
        let constants = this.props.app.config.constants;

        if (this.props.context === 'onboarding') {
            this.props.closeAllModals();
        }

        if (!this.props.router.location.pathname.match("/config/social") &&
            !this.props.router.location.pathname.match("/content/post") &&
            _.findWhere(this.props.socials.counts, {type: constants.POST_SVC_MASTODON}) !== undefined) {
            this.props.switchView("chooseAcct", constants.POST_SVC_MASTODON);
        } else {
            this.props.switchView("chooseType");
        }

    }

    fields() {
        return [
            {
                id: HANDLE_FIELD,
                label: 'Mastodon Handle',
                placeholder: '@username@server or https://server/@username',
                value: this.state.mastodonHandle,
                error: this.state.error || undefined,
                hint: [
                    'Log into Mastodon and select your profile to find your handle.',
                    'Format: @yourusername@your.server',
                    'Example: @Mastodon@mastodon.social'
                ]
            }
        ];
    }

    render() {
        return (
            <ConnectSocialDialog
                screen="credentials"
                network="mastodon"
                title="Connect Mastodon"
                fields={this.fields()}
                primaryLabel="Connect"
                primaryDisabled={this.state.mastodonHandle.trim() === ''}
                backLabel="Back"
                onFieldChange={(id, value) => {
                    this.setState({
                        mastodonHandle: String(value),
                        // Clearing on edit: the message names what was wrong with
                        // the previous value, so it stops describing this one the
                        // moment a key is pressed.
                        error: ''
                    });
                }}
                onPrimary={this.collectHandle}
                onBack={this.closeAction}
                onClose={this.props.closeAllModals} />
        );
    }
};

MastodonHandle.displayName = "MastodonHandle";

export default MastodonHandle;
